import React, { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useStagePageContext, useVenuePageContext } from "Venues/contexts";
import { IEquipmentSubcategoryItem } from "Venues/types";

import { dataMetaSelector, dispatchGetStageEquipmentAction } from "Venues/state";
import { EquipmentTable, EquipmentTableType } from "Venues/components";
import { getStageEquipmentKey, useSelectStageEquipment } from "Venues/utils";
import { Spacer } from "shared/layout";
import { Title, RTEContent } from "shared/components";

interface EquipmentBlockSubcategoryProps {
  subcategory: IEquipmentSubcategoryItem;
}

export const EquipmentBlockSubcategory: React.FC<EquipmentBlockSubcategoryProps> = ({
  subcategory,
}) => {
  const { venueId } = useVenuePageContext();
  const { stage, stageId } = useStagePageContext();

  const { pagination, equipment } = useSelectStageEquipment(stageId || "", subcategory.category);

  const { api: getApiMeta } = useSelector(
    dataMetaSelector(getStageEquipmentKey(stageId, subcategory.category))
  );
  const dispatch = useDispatch();

  const fetchEquipment = useCallback(
    (page?: number) => {
      if (!stageId) {
        return;
      }

      dispatchGetStageEquipmentAction(dispatch, {
        venue: venueId,
        stage: stageId,
        category: subcategory.category,
        page,
      });
    },
    [dispatch, stageId, venueId, subcategory.category]
  );

  const fetchedEquipmentOnce = useRef(false);
  useEffect(() => {
    // if subcategory has no equipment, don't fetch equipment
    if (!stageId || !subcategory.amount || fetchedEquipmentOnce.current) {
      return;
    }

    fetchedEquipmentOnce.current = true;

    fetchEquipment();
  }, [stageId, subcategory.amount, fetchEquipment]);

  if (stage === null || !stageId) return null;
  if (!subcategory?.text && !subcategory?.amount) return null;

  return (
    <Spacer size="m">
      <Title asComponent="h3" size="m" title={subcategory.label} />
      <RTEContent content={subcategory.text} />
      <EquipmentTable
        type={EquipmentTableType.MoreInfo}
        showEditActions={false}
        nbSkeletonRows={subcategory.amount}
        equipment={equipment}
        apiStatus={getApiMeta?.status}
        showEmpty={false}
        pager={
          pagination && {
            ...pagination,
            onPageChange: fetchEquipment,
          }
        }
        fetchEquipment={fetchEquipment}
      />
    </Spacer>
  );
};
